import {
  createTriggerActionRuntime,
  type ActionOpportunityPrerequisiteSnapshot,
  type ActionOpportunitySnapshot,
  type ActiveActionAuthoritySnapshot,
  type ActiveServiceContinuityBaseline,
  type ActorSample,
  type AuthorizationBaseline,
  type CollectWorkFact,
  type DefinitionHandle,
  type DirectTransferWorkFact,
  type Phase7ArrivalObservation,
  type PreauthorizedActionGrant,
  type TendWorkFact,
} from "../actions/index.ts";
import { OWNER_KIND, type LeaseBook, type OwnerRef, type TransferCommand } from "../transactions/index.ts";
import type {
  InstalledManualChainS04Options,
  InstalledManualChainS04Result,
  ManualChainS04CommitRequest,
  ManualChainS04Port,
} from "./types.ts";
import { createEmptyActionLifecycleSlice } from "./state.ts";

type SupportedVerb = ManualChainS04CommitRequest["verb"];

function ownerRef(request: Readonly<ManualChainS04CommitRequest>, ownerKind: number, ownerId: number): OwnerRef {
  const owner = request.ledger.owners.find((candidate) => candidate.ownerKind === ownerKind && candidate.ownerId === ownerId);
  if (owner === undefined) throw new Error("S04 adapter owner missing");
  return Object.freeze({ ownerKind: owner.ownerKind, ownerId: owner.ownerId, version: owner.version });
}

function staticDefinition(verb: SupportedVerb) {
  if (verb === "stage") return Object.freeze({ definitionId: 1 as const, triggerId: 1, semanticRuleId: 2, serviceId: 21, serviceKind: "pen-stage-corn" as const, actionKind: "stage-batch" as const, authorityKind: "s03-transfer" as const, timingProfileId: 2, priorityClassRank: 4 });
  if (verb === "tend") return Object.freeze({ definitionId: 2 as const, triggerId: 2, semanticRuleId: 2, serviceId: 22, serviceKind: "pen-tend" as const, actionKind: "tend-wave" as const, authorityKind: "node-reaction" as const, timingProfileId: 3, priorityClassRank: 5 });
  return Object.freeze({ definitionId: 3 as const, triggerId: 3, semanticRuleId: 3, serviceId: 31, serviceKind: "pen-collect-egg" as const, actionKind: "collect-unit" as const, authorityKind: "s03-transfer" as const, timingProfileId: 1, priorityClassRank: 5 });
}

function buildCarrier(request: Readonly<ManualChainS04CommitRequest>, startTick: number) {
  const def = staticDefinition(request.verb);
  const source = request.verb === "stage"
    ? ownerRef(request, OWNER_KIND.ACTOR_CARGO, 1)
    : request.verb === "collect"
      ? ownerRef(request, OWNER_KIND.PEN_OUTPUT, 2)
      : null;
  const destination = request.verb === "stage"
    ? ownerRef(request, OWNER_KIND.PEN_STAGING, 2)
    : request.verb === "collect"
      ? ownerRef(request, OWNER_KIND.ACTOR_CARGO, 1)
      : null;
  const transferCommand: TransferCommand | null = request.verb === "tend" ? null : Object.freeze({
    actorId: request.actorId,
    actionId: request.actionId,
    itemIds: Object.freeze([...request.itemIds]),
    source: source!, destination: destination!,
    expectedItemKind: request.verb === "stage" ? 1 : 2,
    capacityClaim: request.itemIds.length,
    navRevision: null,
    reservationLeaseId: request.actionId,
    idempotencyKey: 1,
    cancelled: false,
  });
  const first = request.ledger.items.find((item) => item.itemId === request.itemIds[0]);
  const direct: DirectTransferWorkFact | CollectWorkFact | null = transferCommand === null ? null : Object.freeze(request.verb === "stage" ? {
    kind: "direct-transfer" as const, itemKind: "corn" as const, expectedItemKind: 1 as const,
    quantity: request.itemIds.length, transferCommand,
  } : {
    kind: "collect" as const, itemKind: "egg" as const, expectedItemKind: 2 as const,
    lineageId: first?.lineageId ?? 1,
    availableOwnedEggItemIds: Object.freeze([...request.itemIds]), currentEggItemId: request.itemIds[0]!,
    quantity: 1 as const, transferCommand,
  });
  const staged = ownerRef(request, OWNER_KIND.PEN_STAGING, 2);
  const output = ownerRef(request, OWNER_KIND.PEN_OUTPUT, 2);
  const tend: TendWorkFact | null = request.verb !== "tend" ? null : Object.freeze({
    kind: "tend", waveId: request.workOrderId, waveIndex: 1, activeChickenCount: 4,
    stagedExpectedItemKind: 1, stagedOwner: staged,
    remainingBatchCornItemIds: Object.freeze([...request.itemIds]),
    remainingBatchCornLineageIds: Object.freeze(request.itemIds.map((id) => request.ledger.items.find((item) => item.itemId === id)!.lineageId)),
    currentWaveCornItemIds: Object.freeze([...request.itemIds]), outputExpectedItemKind: 2,
    outputOwner: output, remainingBatchOutputCapacityClaim: request.itemIds.length,
    currentWaveOutputCapacityClaim: request.itemIds.length,
    remainingBatchQuantity: request.itemIds.length, quantity: request.itemIds.length,
  });
  const prerequisite: ActionOpportunityPrerequisiteSnapshot = Object.freeze({
    kind: request.verb,
    quantity: request.verb === "collect" ? 1 : request.itemIds.length,
    itemKind: request.verb === "stage" ? "corn" : request.verb === "collect" ? "egg" : null,
    expectedItemKind: request.verb === "stage" ? 1 : request.verb === "collect" ? 2 : null,
    itemIds: request.verb === "tend" ? null : Object.freeze([...request.itemIds]),
    collectLineageId: request.verb === "collect" ? first?.lineageId ?? 1 : null,
    sourceOwner: source, destinationOwner: destination,
    requiresReservationLease: true,
    tendWorkFact: tend, purchaseWorkFact: null,
  });
  const key = Object.freeze({
    actorId: request.actorId, bodyId: request.bodyId, definitionId: def.definitionId,
    triggerId: def.triggerId, nodeId: 2, serviceId: def.serviceId, serviceVersion: 1,
  });
  const membership = Object.freeze({
    ...key, semanticRuleId: def.semanticRuleId, serviceKind: def.serviceKind,
    actionKind: def.actionKind, authorityKind: def.authorityKind,
    timingProfileId: def.timingProfileId, priorityClassRank: def.priorityClassRank,
    reservedTaskId: null, workOrderId: null,
    purchaseOfferId: null, purchaseEligibility: null, prerequisite,
  });
  const baseline: AuthorizationBaseline = Object.freeze({
    authorizationKey: key,
    membershipBaseline: membership,
    priorityBasis: Object.freeze({ basePriorityClassRank: def.priorityClassRank as 4 | 5, tendRecoveryProof: null, farmHandReservationProof: null }),
  });
  const observation: Phase7ArrivalObservation = Object.freeze({ observationTick: startTick, authorizationKey: key, status: "eligible", arrivalEligibleTick: startTick });
  const opportunity: ActionOpportunitySnapshot = Object.freeze({
    matchKey: Object.freeze({ opportunityTick: startTick, authorizationKey: key }),
    authorizationBaseline: baseline,
    arrivalObservation: observation,
    distanceSquaredMicroWU2: "0",
    opportunityTick: startTick,
    actorId: membership.actorId,
    bodyId: membership.bodyId,
    definitionId: membership.definitionId,
    triggerId: membership.triggerId,
    nodeId: membership.nodeId,
    serviceId: membership.serviceId,
    serviceVersion: membership.serviceVersion,
    semanticRuleId: membership.semanticRuleId,
    serviceKind: membership.serviceKind,
    actionKind: membership.actionKind,
    authorityKind: membership.authorityKind,
    timingProfileId: membership.timingProfileId,
    basePriorityClassRank: def.priorityClassRank as 4 | 5,
    priorityClassRank: membership.priorityClassRank,
    arrivalEligibleTick: startTick,
    reservedTaskId: membership.reservedTaskId,
    workOrderId: membership.workOrderId,
    purchaseOfferId: membership.purchaseOfferId,
    purchaseEligibility: membership.purchaseEligibility,
    prerequisite: membership.prerequisite,
  });
  const grant: PreauthorizedActionGrant = Object.freeze({
    grantTick: startTick, authorizationBaseline: baseline, provenanceKind: "scored-preauthorization", sourceSelectionRequest: null,
    serviceId: def.serviceId, serviceVersion: 1, enabled: true, actorId: request.actorId,
    bodyId: request.bodyId, definitionId: def.definitionId, triggerId: def.triggerId,
    nodeId: 2, actionId: request.actionId, semanticRuleId: def.semanticRuleId,
    serviceKind: def.serviceKind, actionKind: def.actionKind, authorityKind: def.authorityKind,
    timingProfileId: def.timingProfileId, priorityClassRank: def.priorityClassRank,
    purchaseOfferId: null, purchaseEligibility: null,
    reservationLeaseId: request.actionId,
    reservationLeaseVersion: 0,
    reservedTaskId: null, workOrderId: null,
    quantity: request.verb === "collect" ? 1 : request.itemIds.length,
    workFact: (tend ?? direct)!,
  });
  const continuity: ActiveServiceContinuityBaseline = Object.freeze({
    authorizationKey: key, semanticRuleId: def.semanticRuleId, serviceKind: def.serviceKind,
    actionKind: def.actionKind, authorityKind: def.authorityKind, timingProfileId: def.timingProfileId,
    reservedTaskId: null, workOrderId: null, purchaseOfferId: null,
    purchaseEligibility: null, prerequisite,
  });
  const actor: ActorSample = Object.freeze({
    actorId: request.actorId, bodyId: request.bodyId, humanoidAuthorityClass: "HumanoidActor",
    actorKind: "Player", enabled: true, present: true, bodyVisible: true,
    xMicroWU: 1_000_000, approachYMicroWU: 0,
    zMicroWU: request.verb === "collect" ? -1_273_000 : -1_473_000,
  });
  const leaseOwner = request.verb === "stage" ? destination! : request.verb === "collect" ? destination! : output;
  const leaseBook: LeaseBook = Object.freeze({
    nextLeaseId: request.actionId + 1,
    leases: Object.freeze([Object.freeze({
      leaseId: request.actionId, actorId: request.actorId, actionId: request.actionId,
      ownerKind: leaseOwner.ownerKind, ownerId: leaseOwner.ownerId, ownerVersion: leaseOwner.version,
      capacity: request.itemIds.length, version: 0, expiresTick: startTick + 1_000,
    })]),
  });
  return Object.freeze({ def, baseline, observation, opportunity, grant, continuity, actor, transferCommand, leaseBook });
}

export async function createInstalledManualChainS04Port(
  options: Readonly<InstalledManualChainS04Options>,
): Promise<InstalledManualChainS04Result> {
  const runtime = createTriggerActionRuntime(options.dependencies);
  const installation = await runtime.installDefinitions(options.installBatch);
  if (installation.status !== "installed") return Object.freeze({ status: installation.status, port: null });
  const handles = installation.handles;
  const port: ManualChainS04Port = Object.freeze({
    commit: (request: Readonly<ManualChainS04CommitRequest>) => {
      try {
        const delay = request.verb === "tend" ? 36 : request.verb === "stage" ? 4 + 2 * request.itemIds.length : 6;
        const startTick = 1;
        const carrier = buildCarrier(request, startTick);
        let phase8 = runtime.reducePhase8({
          authorityTick: startTick, previousSlice: createEmptyActionLifecycleSlice(),
          actorSamples: Object.freeze([carrier.actor]), definitionHandles: handles,
          opportunities: Object.freeze([carrier.opportunity]), phase7ArrivalObservations: Object.freeze([carrier.observation]),
          preauthorizedActionGrants: Object.freeze([carrier.grant]), activeActionAuthorities: Object.freeze([]),
          purchaseEligibilitySnapshots: Object.freeze([]),
        });
        for (let tick = 2; tick <= delay + 1; tick += 1) {
          const consumed = runtime.reducePhase9({ authorityTick: tick - 1, phase8Slice: phase8.slice, ledgerState: request.ledger, leaseBook: carrier.leaseBook });
          const active: ActiveActionAuthoritySnapshot = Object.freeze({
            authorityTick: tick, actorId: request.actorId, bodyId: request.bodyId,
            actionId: request.actionId, originalBaseline: carrier.grant,
            continuityBaseline: carrier.continuity, currentContinuityBaseline: carrier.continuity,
            authorityStatus: "current", releaseProof: null, currentServiceVersion: 1,
            explicitCancel: false,
            commandObservation: Object.freeze({ status: carrier.transferCommand === null ? "not-applicable" : "present-exact", observedCommand: carrier.transferCommand }),
            leaseObservation: Object.freeze({
              status: "present-exact",
              observedLease: Object.freeze({
                leaseId: carrier.leaseBook.leases[0]!.leaseId,
                leaseVersion: carrier.leaseBook.leases[0]!.version,
                actorId: carrier.leaseBook.leases[0]!.actorId,
                actionId: carrier.leaseBook.leases[0]!.actionId,
                ownerKind: carrier.leaseBook.leases[0]!.ownerKind,
                ownerId: carrier.leaseBook.leases[0]!.ownerId,
                ownerVersion: carrier.leaseBook.leases[0]!.ownerVersion,
                capacity: carrier.leaseBook.leases[0]!.capacity,
                expiresTick: carrier.leaseBook.leases[0]!.expiresTick,
              }),
            }),
          });
          phase8 = runtime.reducePhase8({
            authorityTick: tick, previousSlice: consumed.slice, actorSamples: Object.freeze([carrier.actor]),
            definitionHandles: handles, opportunities: Object.freeze([]),
            phase7ArrivalObservations: Object.freeze([Object.freeze({ ...carrier.observation, observationTick: tick })]),
            preauthorizedActionGrants: Object.freeze([]), activeActionAuthorities: Object.freeze([active]), purchaseEligibilitySnapshots: Object.freeze([]),
          });
        }
        const accepted = request.verb === "tend"
          ? phase8.output.tendReady.some((ready) => ready.actorId === request.actorId && ready.actionId === request.actionId)
          : phase8.output.previewRequests.some((preview) => preview.actorId === request.actorId && preview.actionId === request.actionId);
        return Object.freeze({ accepted, lifecycleSlice: phase8.slice });
      } catch {
        return Object.freeze({ accepted: false, lifecycleSlice: request.lifecycleSlice });
      }
    },
  });
  return Object.freeze({ status: "installed", port });
}
